import { useState, useMemo, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import { useTimeTrackingContext } from '@/hooks/useTimeTrackingContext'
import { useTimeEntriesCache } from '@/hooks/useTimeEntriesCache'
import { useProjects } from '@/hooks/useProjects'
import { useUserSettingsContext } from '@/hooks/useUserSettingsContext'
import { TimeEntry, UpdateTimeEntryInput, CreateTimeEntryInput } from '@/services/timeTracking'
import { CreateTimeEntryRequest, UpdateTimeEntryRequest } from '../../../shared/types'
import CustomPagination from '@/components/CustomPagination'
import ConfirmationDialog from '@/components/ConfirmationDialog'
import CustomDropdown from '@/components/CustomDropdown'
import TimeEntryForm from '@/components/TimeEntryForm'
import TimezoneIndicator from '@/components/TimezoneIndicator'
import { detectOverlappingEntries } from '@/utils/overlapDetection'
import { getEffectiveTimezone } from '@/utils/timezoneOptions'
import { computeBudgetViolatingEntryIds } from '@/utils/budgetUtils'
import { isLightColor, LIGHT_COLOR_CONTOUR_FILTER } from '@/utils/colorUtils'
import { getWeekKey } from '../../../shared/utils/dateUtils'

type GroupByPeriod = 'day' | 'week' | 'month' | 'year' | 'outOfBudget'

interface GroupedEntries {
  [date: string]: TimeEntry[]
}

// Adapter function to convert service TimeEntry to shared TimeEntry format
const convertToSharedTimeEntry = (serviceEntry: TimeEntry) => ({
  ...serviceEntry,
  isRunning: !serviceEntry.endTime
})

export default function Entries() {
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([])
  const [groupBy, setGroupBy] = useState<GroupByPeriod>('day')
  const [showNewEntryForm, setShowNewEntryForm] = useState(false)
  const [editingEntry, setEditingEntry] = useState<TimeEntry | null>(null)
  const [deletingEntryId, setDeletingEntryId] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10) // Default page size
  const [loading] = useState(false)
  const [recentlyEditedEntries, setRecentlyEditedEntries] = useState<Set<string>>(new Set())

  const timeTracking = useTimeTrackingContext()
  const { allEntries, loading: cacheLoading, loadProgress } = useTimeEntriesCache()
  const projects = useProjects()
  const { displaySettings, timezoneSettings, effectiveWeekStartDay } = useUserSettingsContext()

  // Only active projects — used for creation forms and the project filter dropdown
  // (allProjects only contains active projects — archived ones are not loaded here)
  const activeProjects = useMemo(
    () => (projects.allProjects || []).filter(p => p.status === 'active' || !p.status),
    [projects.allProjects]
  )

  // Handle URL parameters for project pre-selection
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const projectId = urlParams.get('project')

    if (projectId && projects.allProjects && projects.allProjects.length > 0) {
      // Check if the project exists
      const projectExists = projects.allProjects.some(p => p.id === projectId)
      if (projectExists) {
        setSelectedProjectIds([projectId])
      }
    }
  }, [projects.allProjects])

  // Handle URL parameter for groupBy pre-selection (once on mount)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const groupByParam = urlParams.get('groupBy')
    if (groupByParam && ['day', 'week', 'month', 'year', 'outOfBudget'].includes(groupByParam)) {
      setGroupBy(groupByParam as GroupByPeriod)
    }
  }, [])

  // Filter entries: exclude archived project entries, then apply project filter if active
  const filteredEntries = useMemo(() => {
    let entries = allEntries;
    // Exclude entries whose project is archived/unknown — guard against loading flash
    if (!projects.loading) {
      const activeProjectIds = new Set(activeProjects.map(p => p.id));
      entries = entries.filter(e => !e.projectId || activeProjectIds.has(e.projectId));
    }
    if (selectedProjectIds.length > 0) {
      entries = entries.filter(entry => selectedProjectIds.includes(entry.projectId || ''))
    }
    return entries
  }, [allEntries, selectedProjectIds, activeProjects, projects.loading])

  // Always compute budget-violating entry IDs (used for warnings display and outOfBudget grouping)
  const budgetViolatingEntryIds = useMemo(() => {
    return computeBudgetViolatingEntryIds(
      filteredEntries,
      projects.allProjects || [],
      timeTracking.getCurrentDuration
    )
  }, [filteredEntries, projects.allProjects, timeTracking.getCurrentDuration])

  const hasOutOfBudgetEntries = budgetViolatingEntryIds.size > 0

  // Group entries based on selected grouping
  const groupedEntries = useMemo(() => {
    const groups: GroupedEntries = {}

    const getGroupKey = (entry: TimeEntry): string | null => {
      if (groupBy === 'outOfBudget') {
        return budgetViolatingEntryIds.has(entry.id) ? 'out-of-budget' : null
      }

      const date = new Date(entry.startTime)
      const effectiveTimezone = getEffectiveTimezone(entry.timezone);

      switch (groupBy) {
        case 'day':
          return date.toDateString()
        case 'week': {
          // Use shared getWeekKey function for consistent week calculations with user's week start day setting
          return getWeekKey(date, effectiveTimezone, effectiveWeekStartDay)
        }
        case 'month':
          return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
        case 'year':
          return date.getFullYear().toString()
        default:
          return date.toDateString()
      }
    }

    filteredEntries.forEach(entry => {
      const groupKey = getGroupKey(entry)
      if (groupKey === null) return
      if (!groups[groupKey]) {
        groups[groupKey] = []
      }
      groups[groupKey].push(entry)
    })

    // Sort entries within each group by start time (newest first)
    Object.keys(groups).forEach(groupKey => {
      groups[groupKey].sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime())
    })

    return groups
  }, [filteredEntries, groupBy, effectiveWeekStartDay, budgetViolatingEntryIds])

  const sortedDates = useMemo(() => {
    if (groupBy === 'outOfBudget') {
      return ['out-of-budget'].filter(key => key in groupedEntries)
    }
    return Object.keys(groupedEntries).sort((a, b) => new Date(b).getTime() - new Date(a).getTime())
  }, [groupedEntries, groupBy])

  // Detect overlapping entries within the same project using shared utility
  // Uses "benefit of doubt" approach: minute precision for form entries, second precision for timer entries
  const overlappingEntryIds = useMemo(() => {
    return detectOverlappingEntries(filteredEntries);
  }, [filteredEntries]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize)
    setCurrentPage(1) // Reset to first page when changing page size
  }

  const handleEdit = (entry: TimeEntry) => {
    setEditingEntry(entry)
  }

  const handleUpdate = async (data: CreateTimeEntryRequest | UpdateTimeEntryRequest) => {
    if (!editingEntry) return
    
    // Convert shared type to service type
    const serviceUpdates: UpdateTimeEntryInput = {
      description: data.description,
      projectId: data.projectId,
      startTime: data.startTime,
      endTime: data.endTime,
      location: data.location
    }
    
    await timeTracking.updateTimeEntry(editingEntry.id, serviceUpdates)
    
    // Add to recently edited entries for highlighting
    setRecentlyEditedEntries(prev => new Set([...prev, editingEntry.id]))
    
    // Scroll to the edited card
    setTimeout(() => {
      const cardElement = document.querySelector(`[data-entry-id="${editingEntry.id}"]`)
      if (cardElement) {
        cardElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center',
          inline: 'nearest'
        })
      }
    }, 100)
    
    // Remove highlighting after 3 seconds
    setTimeout(() => {
      setRecentlyEditedEntries(prev => {
        const newSet = new Set(prev)
        newSet.delete(editingEntry.id)
        return newSet
      })
    }, 3000)
    
    setEditingEntry(null)
  }

  const handleEntryClick = (entry: TimeEntry) => {
    handleEdit(entry)
  }

  const handleCreateNewEntry = async (data: CreateTimeEntryRequest | UpdateTimeEntryRequest) => {
    // Convert shared type to service type
    const serviceInput: CreateTimeEntryInput & { endTime: string } = {
      description: data.description!,
      projectId: data.projectId,
      startTime: data.startTime!,
      endTime: data.endTime || '',
      location: data.location,
      timezone: (data as CreateTimeEntryRequest).timezone
    }

    const result = await timeTracking.createTimeEntry(serviceInput)
    
    // Add to recently edited entries for highlighting (treating new entries as "edited" for the glow effect)
    setRecentlyEditedEntries(prev => new Set([...prev, result.timeEntry.id]))
    
    // Scroll to the newly created card
    setTimeout(() => {
      const cardElement = document.querySelector(`[data-entry-id="${result.timeEntry.id}"]`)
      if (cardElement) {
        cardElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center',
          inline: 'nearest'
        })
      }
    }, 100)
    
    // Remove highlighting after 3 seconds
    setTimeout(() => {
      setRecentlyEditedEntries(prev => {
        const newSet = new Set(prev)
        newSet.delete(result.timeEntry.id)
        return newSet
      })
    }, 3000)
    
    setShowNewEntryForm(false)
  }

  const handleDelete = (entryId: string) => {
    setDeletingEntryId(entryId)
  }

  const handleConfirmDelete = async () => {
    if (!deletingEntryId) return
    await timeTracking.deleteTimeEntry(deletingEntryId)
    setDeletingEntryId(null)
  }

  const handleCancelDelete = () => {
    setDeletingEntryId(null)
  }

  const handleResume = async (entry: TimeEntry) => {
    try {
      // Use user's preferred timezone (or browser default if not set)
      const effectiveTimezone = getEffectiveTimezone(timezoneSettings.preferredTimezone);
      await timeTracking.startTimeEntry({
        description: entry.description,
        projectId: entry.projectId,
        tags: entry.tags,
        location: entry.location,
        timezone: effectiveTimezone
      })
    } catch {
      // Error is handled by the hook
    }
  }

  const formatTime = (dateString: string) => {
    const date = new Date(dateString)
    // Ensure we're working with the local timezone
    return date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false, // European format (24-hour)
    })
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)

    if (date.toDateString() === today.toDateString()) {
      return 'Today'
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday'
    } else {
      return date.toLocaleDateString([], {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      })
    }
  }

  const getProjectForEntry = (entry: TimeEntry) => {
    return (projects.allProjects || []).find(p => p.id === entry.projectId)
  }

  const getEntryDuration = (entry: TimeEntry) => {
    if (!entry.endTime) {
      return timeTracking.getCurrentDuration(entry)
    }
    // Convert milliseconds to seconds for formatDuration
    return Math.floor((new Date(entry.endTime).getTime() - new Date(entry.startTime).getTime()) / 1000)
  }

  const formatGroupHeader = (groupKey: string) => {
    switch (groupBy) {
      case 'day': {
        const date = new Date(groupKey)
        const today = new Date()
        const yesterday = new Date(today)
        yesterday.setDate(yesterday.getDate() - 1)

        if (date.toDateString() === today.toDateString()) {
          return 'Today'
        } else if (date.toDateString() === yesterday.toDateString()) {
          return 'Yesterday'
        } else {
          return date.toLocaleDateString([], {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
          })
        }
      }
      case 'week': {
        // Parse week key format: "YYYY-WNN"
        const match = groupKey.match(/^(\d{4})-W(\d{2})$/)
        if (!match) {
          return groupKey // Fallback if format is unexpected
        }

        const year = parseInt(match[1], 10)
        const weekNum = parseInt(match[2], 10)

        // Calculate the start date of this week based on the week start day setting
        let weekStart: Date

        if (effectiveWeekStartDay === 1) {
          // ISO 8601: Monday start
          // Week 1 contains the first Thursday of the year
          const jan4 = new Date(year, 0, 4)
          const jan4DayOfWeek = jan4.getDay() || 7 // Convert Sunday from 0 to 7
          const week1Monday = new Date(jan4)
          week1Monday.setDate(jan4.getDate() - (jan4DayOfWeek - 1))

          weekStart = new Date(week1Monday)
          weekStart.setDate(week1Monday.getDate() + (weekNum - 1) * 7)
        } else {
          // US-style: Sunday start
          // Week 1 contains January 1st
          const jan1 = new Date(year, 0, 1)
          const jan1DayOfWeek = jan1.getDay()
          const firstSunday = new Date(year, 0, 1 - jan1DayOfWeek)

          weekStart = new Date(firstSunday)
          weekStart.setDate(firstSunday.getDate() + (weekNum - 1) * 7)
        }

        const weekEnd = new Date(weekStart)
        weekEnd.setDate(weekStart.getDate() + 6)

        return `${weekStart.toLocaleDateString([], { month: 'short', day: 'numeric' })} - ${weekEnd.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}`
      }
      case 'month': {
        const date = new Date(groupKey)
        return date.toLocaleDateString([], { month: 'long', year: 'numeric' })
      }
      case 'year': {
        return groupKey
      }
      case 'outOfBudget': {
        return 'Out of Budget'
      }
      default: {
        const date = new Date(groupKey)
        return date.toLocaleDateString()
      }
    }
  }

  const calculateGroupTotal = (entries: TimeEntry[]) => {
    return entries.reduce((total, entry) => total + getEntryDuration(entry), 0)
  }

  // Pagination logic
  const totalItems = sortedDates.length
  const totalPages = Math.ceil(totalItems / pageSize)
  const startIndex = (currentPage - 1) * pageSize
  const endIndex = startIndex + pageSize
  const currentDates = sortedDates.slice(startIndex, endIndex)
  const hasNextPage = currentPage < totalPages
  const hasPrevPage = currentPage > 1

  if (cacheLoading || projects.loading) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">
          <div data-testid="loading-spinner" className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          {cacheLoading && loadProgress.estimated > 0 && (
            <p className="mt-4 text-sm text-gray-600">
              Loading entries: {loadProgress.loaded} / {loadProgress.estimated}
            </p>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Filters and Actions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-4">
            <button
              onClick={() => setShowNewEntryForm(true)}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              New Entry
            </button>
            {/* Project Filter */}
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500">Project:</span>
              <CustomDropdown
                id="entries-project"
                label=""
                placeholder="All Projects"
                options={activeProjects.map(project => ({ value: project.id, label: project.name }))}
                selectedValues={selectedProjectIds}
                onSelectionChange={setSelectedProjectIds}
                multiSelect={true}
                maxSelections={6}
                showClearAll={true}
                clearAllText="Clear All"
                searchable={true}
                searchPlaceholder="Search projects..."
                minWidth="min-w-[250px]"
              />
            </div>

            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500">Group by:</span>
              <CustomDropdown
                id="groupby"
                label=""
                placeholder="Select grouping"
                options={[
                  { value: 'day', label: 'Day' },
                  { value: 'week', label: 'Week' },
                  { value: 'month', label: 'Month' },
                  { value: 'year', label: 'Year' },
                  { value: 'outOfBudget', label: 'Out of Budget', disabled: !hasOutOfBudgetEntries }
                ]}
                selectedValues={[groupBy]}
                onSelectionChange={(values) => setGroupBy(values[0] as GroupByPeriod)}
                multiSelect={false}
              />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <CustomPagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={totalItems}
              hasNextPage={hasNextPage}
              hasPrevPage={hasPrevPage}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              pageSize={pageSize}
              className="mb-0"
              transparency="medium"
              minWidth="min-w-[400px]"
            />
          </div>
        </div>
      </div>

      {/* Entries Display */}
      {sortedDates.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <div className="text-gray-500 mb-4">No time entries found</div>
          <p className="text-sm text-gray-400 mb-6">
            Start tracking your time to see entries here
          </p>
          <button
            onClick={() => setShowNewEntryForm(true)}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            Create Your First Entry
          </button>
        </div>
      ) : (
        <div className="space-y-8">
          {currentDates.map(date => (
            <div key={date} className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900">
                    {formatGroupHeader(date)}
                  </h2>
                  <div className="text-sm text-gray-500">
                    {timeTracking.formatDuration(calculateGroupTotal(groupedEntries[date]))}
                  </div>
                </div>
              </div>
              
              <div className="px-6 pt-8 pb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {groupedEntries[date].map((entry) => {
                    const project = getProjectForEntry(entry)
                    const duration = getEntryDuration(entry)
                    
                    return (
                      <div
                        key={entry.id}
                        data-entry-id={entry.id}
                        onClick={() => handleEntryClick(entry)}
                        className={`flex flex-col rounded-lg shadow-sm border border-gray-200 p-4 cursor-pointer hover:shadow-md transition-all duration-300 ${
                          !entry.endTime ? 'bg-blue-50 border-blue-200' : 'bg-white'
                        }`}
                        style={{
                          backgroundColor: recentlyEditedEntries.has(entry.id) && project 
                            ? `${project.color}20` // 20 = 12% opacity in hex
                            : undefined,
                          borderColor: recentlyEditedEntries.has(entry.id) && project 
                            ? `${project.color}40` // 40 = 25% opacity in hex
                            : undefined,
                          boxShadow: recentlyEditedEntries.has(entry.id) 
                            ? `0 0 0 2px ${project?.color || '#3b82f6'}40` // Ring effect with project color
                            : undefined
                        }}
                      >
                         {/* Project */}
                         <div className="flex items-start justify-between mb-3">
                           <div className="flex items-center space-x-3 min-w-0 flex-1">
                             {project && (
                               <div className="flex items-center space-x-3 min-w-0 flex-1">
                                 <div
                                   className="w-1 h-6 rounded-full flex-shrink-0"
                                   style={{ backgroundColor: project.color }}
                                 />
                                 <span className="text-sm font-medium text-gray-900 truncate">
                                   {project.name}
                                 </span>
                               </div>
                             )}
                             {!project && (
                               <span className="text-sm text-gray-500">No Project</span>
                             )}
                           </div>
                         </div>

                        {/* Description */}
                        <div className="mb-3">
                          <p className="text-sm text-gray-900 line-clamp-2">
                            {entry.description}
                          </p>
                        </div>

                        <div className="border-t border-gray-100 my-3" />

                        {/* Location • Duration */}
                        <div className="flex items-center flex-wrap gap-x-2 gap-y-1 mb-3 text-xs text-gray-600">
                          {entry.location && (
                            <>
                              <span className="inline-flex items-center gap-1">
                                <svg className="w-3 h-3 flex-shrink-0" style={{ color: project?.color || '#6B7280' }} fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                                </svg>
                                {entry.location}
                              </span>
                              <span className="text-gray-300 select-none">•</span>
                            </>
                          )}
                          <span className="inline-flex items-center gap-1">
                            <svg className="w-3 h-3 flex-shrink-0" style={{ color: project?.color || '#6B7280' }} fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                            </svg>
                            {timeTracking.formatDuration(duration)}
                          </span>
                        </div>

                        {/* Date • Time range + indicators */}
                        <div className="flex items-center flex-wrap gap-x-2 gap-y-1 mb-4 text-xs text-gray-600">
                          <span className="inline-flex items-center gap-1 flex-shrink-0">
                            <svg className="w-3 h-3 flex-shrink-0" style={{ color: project?.color || '#6B7280' }} fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                              <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                            </svg>
                            {formatDate(entry.startTime)}
                          </span>
                          <span className="text-gray-300 select-none flex-shrink-0">•</span>
                          <span className="inline-flex items-center gap-1 flex-shrink-0">
                            <svg className="w-3 h-3 flex-shrink-0" style={{ color: project?.color || '#6B7280' }} fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                            </svg>
                            {formatTime(entry.startTime)}{entry.endTime && ` – ${formatTime(entry.endTime)}`}
                          </span>
                          <TimezoneIndicator
                            entryTimezone={entry.timezone}
                            showIndicator={displaySettings.showTimezoneIndicator}
                            colorClass="text-blue-600"
                          />
                          {displaySettings.showOverlapIndicator && overlappingEntryIds.has(entry.id) && (
                            <span
                              className="inline-flex items-center justify-center w-4 h-4"
                              style={{
                                color: displaySettings.overlapIndicatorColor || "#ef4444",
                                filter: isLightColor(displaySettings.overlapIndicatorColor || "#ef4444") ? LIGHT_COLOR_CONTOUR_FILTER : undefined
                              }}
                              title="This entry overlaps with another entry in the same project"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                            </span>
                          )}
                          {displaySettings.showBudgetWarnings && budgetViolatingEntryIds.has(entry.id) && (
                            <span
                              className="inline-flex items-center justify-center w-4 h-4"
                              style={{
                                color: displaySettings.budgetWarningColor || "#ef4444",
                                filter: isLightColor(displaySettings.budgetWarningColor || "#ef4444") ? LIGHT_COLOR_CONTOUR_FILTER : undefined
                              }}
                              title="This entry's day is out of budget"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                                <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd" />
                              </svg>
                            </span>
                          )}
                        </div>

                        {/* Bottom row: billable icon (left) + actions (right) */}
                        <div className="flex items-center justify-between mt-auto pt-2">
                          {/* Left: billable credit-card icon + running indicator */}
                          <div className="flex items-center space-x-1.5">
                            {project?.billable && (
                              <span title="Billable">
                                <svg className="w-4 h-4 flex-shrink-0 text-gray-400" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                                  <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                                  <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd" />
                                </svg>
                              </span>
                            )}
                            {!entry.endTime && (
                              <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" title="Running" />
                            )}
                          </div>
                          {/* Right: action buttons */}
                          <div className="flex space-x-1">
                           {/* Project jump icon - only show if entry has a project */}
                           {entry.projectId && (
                             <Link
                               to={`/projects?project=${entry.projectId}`}
                               onClick={(e) => e.stopPropagation()}
                               className="text-gray-400 hover:text-purple-600 focus:outline-none focus:text-purple-600"
                               title="View project"
                             >
                               <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5a2 2 0 012-2h6a2 2 0 012 2v2H8V5z" />
                               </svg>
                             </Link>
                           )}
                           <button
                             onClick={(e) => {
                               e.stopPropagation()
                               handleEdit(entry)
                             }}
                             className="text-gray-400 hover:text-blue-600 focus:outline-none focus:text-blue-600"
                             title="Edit entry"
                           >
                             <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                             </svg>
                           </button>
                           {/* Resume button - only show for completed entries when no running entry */}
                           {entry.endTime && !timeTracking.runningEntry && (
                             <button
                               onClick={(e) => {
                                 e.stopPropagation()
                                 handleResume(entry)
                               }}
                               className="text-gray-400 hover:text-green-600 focus:outline-none focus:text-green-600"
                               title="Resume entry"
                             >
                               <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5v14l11-7z" />
                               </svg>
                             </button>
                           )}
                           <button
                             onClick={(e) => {
                               e.stopPropagation()
                               handleDelete(entry.id)
                             }}
                             className="text-gray-400 hover:text-red-600 focus:outline-none focus:text-red-600"
                             title="Delete entry"
                           >
                             <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                             </svg>
                           </button>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      {showNewEntryForm && createPortal(
        <TimeEntryForm
          mode="create"
          projects={activeProjects}
          onSubmit={handleCreateNewEntry}
          onCancel={() => setShowNewEntryForm(false)}
          loading={loading}
          preferredTimezone={timezoneSettings.preferredTimezone}
          weekStartsOn={effectiveWeekStartDay}
        />,
        document.body
      )}

      {editingEntry && createPortal(
        <TimeEntryForm
          mode="edit"
          entry={convertToSharedTimeEntry(editingEntry)}
          projects={activeProjects}
          onSubmit={handleUpdate}
          onCancel={() => setEditingEntry(null)}
          loading={loading}
          weekStartsOn={effectiveWeekStartDay}
        />,
        document.body
      )}

      {deletingEntryId && createPortal(
        <ConfirmationDialog
          isOpen={true}
          title="Delete Time Entry"
          message="Are you sure you want to delete this time entry? This action cannot be undone."
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
        />,
        document.body
      )}
    </div>
  )
} 